import { MutableState } from 'src/replicant/State';
import { CardPendingTrade, CardsState } from './cards.schema';
import {
  CardId,
  CardsRewardId,
  cardCollections,
  cardsAlbumCompleteRewards,
  cardTradeMaxOpenTrades,
  tradeLimits,
} from './cards.ruleset';
import {
  cardsGetOwnedCardCount,
  cardsGetPendingTrade,
  cardsGetPendingTrades,
  cardsGetTradeSentForLimit,
  cardsGetRewardsStatus,
  cardsCanReceiveCard,
  cardsGetTradeReceivedForLimit,
  cardsGetTradeExpiresAt,
  cardsCanStartTrade,
  cardsGetTradesStartedForLimit,
  cardsIsCardValid,
} from './cards.getters';
import { cardsGetTradeRejectAnalyticsEvent } from './cards.analytics';
import { Match2Reward } from '../match2/rewards/match2-rewards.ruleset';
import { getPlayerProfile } from '../profile/profile.getters';
import { sendAnalyticsEvents, type AnalyticsEvent } from '../analytics';

/**
 * Ensure the cards state and pending-trades map exist.
 *
 * @param state Mutable user state to initialize.
 * @returns The initialized cards state.
 */
function cardsEnsureState(state: MutableState): CardsState {
  if (!state.cards) {
    state.cards = {
      seenAt: 0,
      cardsOwned: {},
      cardsReceived: {},
      cardsNewlyAdded: {},
      cardTrades: {
        cardsSent: [],
        cardsReceived: [],
        tradesStartedAt: [],
        pendingTrades: {},
        cardsDiscarded: 0,
      },
    };
  }

  if (!state.cards.cardTrades.pendingTrades) {
    state.cards.cardTrades.pendingTrades = {};
  }

  return state.cards;
}

/**
 * Return this user's escrowed trade cards to their inventory.
 * Trades escrow cards by removing them from inventory and keeping their ids on
 * the pending trade. Proposers get offered cards back; recipients get counter
 * cards back only after they have countered. Completed recipient markers are
 * left untouched.
 *
 * @param state Mutable user state that owns one side of the trade.
 * @param tradeId Pending trade id to return cards for.
 */
function cardsReturnTradeCards(state: MutableState, tradeId: string): void {
  const cards = state.cards;
  const trade = cards?.cardTrades?.pendingTrades?.[tradeId];
  if (!cards || !trade) return;
  if (trade.proposerConfirmedAt) return;

  let cardIds: readonly string[] = [];
  if (trade.proposerId === state.id) {
    cardIds = trade.offeredCardIds;
  } else if (trade.recipientId === state.id && trade.status === 'countered') {
    cardIds = trade.counterCardIds ?? [];
  }

  for (const cardId of cardIds as CardId[]) {
    cards.cardsOwned[cardId] = (cards.cardsOwned[cardId] ?? 0) + 1;
  }
}

/**
 * Update when the user last viewed the cards feature.
 *
 * @param state Mutable user state containing cards state.
 * @param now Timestamp to store as the latest seen time.
 */
export function cardsSetSeenAt(state: MutableState, now: number): void {
  const cards = cardsEnsureState(state);
  cards.seenAt = now;
}

/**
 * Add one card to the user's owned inventory and mark it as newly added.
 *
 * @param state Mutable user state containing cards state.
 * @param args Card grant details.
 * @param args.cardId Card id to add.
 * @param args.now Timestamp associated with the grant.
 */
export function cardsAddCard(
  state: MutableState,
  args: {
    cardId: CardId;
    now: number;
  },
): void {
  const cards = cardsEnsureState(state);

  if (!cards.cardsOwned[args.cardId]) {
    cards.cardsOwned[args.cardId] = 0;
  }

  cards.cardsOwned[args.cardId] += 1;

  if (!cards.cardsNewlyAdded[args.cardId]) {
    cards.cardsNewlyAdded[args.cardId] = 0;
  }

  cards.cardsNewlyAdded[args.cardId] += 1;
}

/**
 * Send one card from the user's inventory.
 * This removes one owned copy and records the send in the current send-limit
 * window.
 *
 * @param state Mutable user state containing cards state.
 * @param args Card send details.
 * @param args.cardId Card id to send.
 * @param args.now Timestamp used for limit checks and history.
 * @throws When the send limit is reached or the user has fewer than two copies.
 */
export function cardsSendCard(
  state: MutableState,
  args: {
    cardId: CardId;
    now: number;
  },
): void {
  const sentInLimit = cardsGetTradeSentForLimit(state, args.now);

  if (sentInLimit.length >= tradeLimits.send.amount) {
    throw new Error('Exceeded card send limit.');
  }

  if (cardsGetOwnedCardCount(state, args.cardId, args.now) < 2) {
    throw new Error(`Not enough copies of ${args.cardId} to send.`);
  }

  cardsRemoveCard(state, args);

  cardsRecordCardSent(state, args.cardId, args.now);
}

/**
 * Record a sent card in the rolling send-limit history.
 * Expired send records are dropped before the new record is appended.
 *
 * @param state Mutable user state containing cards state.
 * @param cardId Card id that was sent.
 * @param now Timestamp to record for the send.
 */
export function cardsRecordCardSent(
  state: MutableState,
  cardId: CardId,
  now: number,
): void {
  const cards = cardsEnsureState(state);
  cards.cardTrades.cardsSent = [
    ...cardsGetTradeSentForLimit(state, now),
    { cardId, timestamp: now },
  ];
}

/**
 * Record a trade initiation in the current JST calendar-day limit history.
 * Entries from earlier JST days are dropped before the new timestamp is
 * appended, lazily keeping the persisted history bounded.
 *
 * @param state Mutable user state containing cards state.
 * @param now Timestamp when the trade was initiated.
 */
function cardsRecordTradeStarted(state: MutableState, now: number): void {
  const cards = cardsEnsureState(state);
  cards.cardTrades.tradesStartedAt = [
    ...cardsGetTradesStartedForLimit(state, now),
    now,
  ];
}

/**
 * Return whether the user has enough copies of a card to put one into a trade.
 *
 * @param state Mutable user state containing cards state.
 * @param cardId Card id to check.
 * @param now Current timestamp forwarded to the inventory getter.
 * @returns True when the user owns at least two copies.
 */
export function cardsCanUseCardInTrade(
  state: MutableState,
  cardId: CardId,
  now: number,
): boolean {
  return cardsGetOwnedCardCount(state, cardId, now) >= 2;
}

/**
 * Remove expired trade-limit history and pending trades.
 * Unfinished pending trades release escrowed cards to the current user before
 * deletion. All trade records expire from their latest `updatedAt` timestamp.
 *
 * @param state Mutable user state containing cards state.
 * @param now Current timestamp used to determine expiration.
 * @returns Trades removed because their response window expired.
 */
export function cardsCleanupExpiredTrades(
  state: MutableState,
  now: number,
): CardPendingTrade[] {
  const cards = state.cards;
  if (!cards?.cardTrades) return [];

  cards.cardTrades.cardsSent = cardsGetTradeSentForLimit(state, now);
  cards.cardTrades.cardsReceived = cardsGetTradeReceivedForLimit(state, now);
  cards.cardTrades.tradesStartedAt = cardsGetTradesStartedForLimit(state, now);

  const pendingTrades = cards.cardTrades.pendingTrades;
  if (!pendingTrades) return [];

  const expiredTrades: CardPendingTrade[] = [];

  for (const [tradeId, trade] of Object.entries(pendingTrades)) {
    const pendingTrade = trade as CardPendingTrade;
    const expiresAt = cardsGetTradeExpiresAt(pendingTrade);

    if (expiresAt <= now) {
      cardsReturnTradeCards(state, tradeId);
      delete pendingTrades[tradeId];
      expiredTrades.push(pendingTrade);
    }
  }

  return expiredTrades;
}

/**
 * Remove expired trades and emit their proposer-side terminal analytics.
 *
 * @param state Mutable user state containing cards and trade state.
 * @param now Current timestamp used to determine expiration.
 * @param api Replicant API used to emit analytics events.
 */
export function cardsCleanupExpiredTradesWithAnalytics(
  state: MutableState,
  now: number,
  api: Parameters<typeof sendAnalyticsEvents>[0],
): void {
  // Release expired escrow and retain the removed trades for terminal events.
  const expiredTrades = cardsCleanupExpiredTrades(state, now);

  // Record expiry only for the proposer mirror that owns the funnel event.
  const events: AnalyticsEvent[] = [];
  for (const trade of expiredTrades) {
    const offerCardId = trade.offeredCardIds[0] as CardId | undefined;
    if (trade.proposerId !== state.id || !offerCardId) continue;

    events.push(
      cardsGetTradeRejectAnalyticsEvent(state, {
        tradeId: trade.tradeId,
        offerCardId,
        friendId: trade.recipientId,
        reason: 'expired',
      }),
    );
  }

  // Expiry cleanup can be a no-op or apply only to recipient mirrors.
  if (events.length === 0) return;

  // Send each proposer-side trade expiry after escrow has been restored.
  sendAnalyticsEvents(api, events);
}

/**
 * Return whether the user can create or accept another open pending trade.
 * Expired trades are cleaned up before the open-trade limit is evaluated.
 *
 * @param state Mutable user state containing cards state.
 * @param now Current timestamp used for cleanup.
 * @returns True when the user is below the open-trade limit.
 */
export function cardsCanOpenTrade(state: MutableState, now: number): boolean {
  cardsCleanupExpiredTrades(state, now);
  return cardsGetPendingTrades(state, now).length < cardTradeMaxOpenTrades;
}

/**
 * Create a proposed card trade as the proposer.
 * The offered card is escrowed by removing it from inventory, the trade start
 * is recorded, and a pending trade is stored under a timestamp-based id.
 *
 * @param state Mutable proposer state.
 * @param args Trade creation details.
 * @param args.recipientId Player id for the trade recipient.
 * @param args.cardId Offered card id.
 * @param args.now Timestamp used for the trade id, history, and validation.
 * @returns The newly created pending trade.
 * @throws When open-trade, daily trade, or inventory limits fail.
 */
export function cardsCreateTrade(
  state: MutableState,
  args: {
    recipientId: string;
    otherUserName?: string;
    otherUserAvatar?: string;
    cardId: CardId;
    now: number;
  },
): CardPendingTrade {
  if (!cardsCanOpenTrade(state, args.now)) {
    throw new Error('Too many open card trades.');
  }

  if (!cardsCanStartTrade(state, args.now)) {
    throw new Error('Exceeded daily card trade limit.');
  }

  if (!cardsCanUseCardInTrade(state, args.cardId, args.now)) {
    throw new Error(`Not enough copies of ${args.cardId} to trade.`);
  }

  const pendingTrades = cardsEnsureState(state).cardTrades.pendingTrades!;
  const baseTradeId = `ctr_${args.now}`;
  let tradeId = baseTradeId;
  let suffix = 1;
  while (pendingTrades[tradeId]) {
    tradeId = `${baseTradeId}_${suffix}`;
    suffix += 1;
  }

  const otherUserProfile = getPlayerProfile(
    state,
    args.otherUserName,
    args.otherUserAvatar,
  );

  const trade: CardPendingTrade = {
    tradeId,
    proposerId: state.id,
    recipientId: args.recipientId,
    otherUserName: otherUserProfile.name,
    otherUserAvatar: otherUserProfile.photo,
    offeredCardIds: [args.cardId],
    status: 'proposed',
    createdAt: args.now,
    updatedAt: args.now,
  };

  cardsRemoveCard(state, { cardId: args.cardId, now: args.now });
  cardsRecordTradeStarted(state, args.now);
  pendingTrades[tradeId] = trade;
  return trade;
}

/**
 * Create a countered trade on the share-link sender side. The sender is the
 * proposer and escrows the shared duplicate card immediately.
 *
 * @param state Mutable sender state.
 * @param args Trade details from a share-link opener.
 * @returns The created countered trade, or undefined when it cannot be opened.
 */
export function cardsCreateCounteredShareTradeAsProposer(
  state: MutableState,
  args: {
    tradeId: string;
    recipientId: string;
    otherUserName?: string;
    otherUserAvatar?: string;
    offeredCardId: CardId;
    counterCardId: CardId;
    now: number;
  },
): CardPendingTrade | undefined {
  if (!cardsCanOpenTrade(state, args.now)) return undefined;
  if (!cardsCanStartTrade(state, args.now)) return undefined;
  if (!cardsCanUseCardInTrade(state, args.offeredCardId, args.now)) {
    return undefined;
  }
  if (!cardsIsCardValid(args.counterCardId)) return undefined;

  const pendingTrades = cardsEnsureState(state).cardTrades.pendingTrades!;
  if (pendingTrades[args.tradeId]) return undefined;

  const otherUserProfile = getPlayerProfile(
    state,
    args.otherUserName,
    args.otherUserAvatar,
  );

  const trade: CardPendingTrade = {
    tradeId: args.tradeId,
    proposerId: state.id,
    recipientId: args.recipientId,
    otherUserName: otherUserProfile.name,
    otherUserAvatar: otherUserProfile.photo,
    offeredCardIds: [args.offeredCardId],
    counterCardIds: [args.counterCardId],
    status: 'countered',
    createdAt: args.now,
    updatedAt: args.now,
    recipientConfirmedAt: args.now,
  };

  cardsRemoveCard(state, { cardId: args.offeredCardId, now: args.now });
  cardsRecordTradeStarted(state, args.now);
  pendingTrades[args.tradeId] = trade;
  return trade;
}

/**
 * Store a countered share-link trade on the opener side and escrow their
 * selected counter card.
 *
 * @param state Mutable opener state.
 * @param args Trade details and sender display profile.
 * @returns The stored trade, or undefined when the opener can no longer trade.
 */
export function cardsAddCounteredShareTradeAsRecipient(
  state: MutableState,
  args: {
    trade: CardPendingTrade;
    otherUserName?: string;
    otherUserAvatar?: string;
    now: number;
  },
): CardPendingTrade | undefined {
  cardsCleanupExpiredTrades(state, args.now);

  const counterCardId = args.trade.counterCardIds?.[0] as CardId | undefined;
  const offeredCardId = args.trade.offeredCardIds[0] as CardId | undefined;
  if (args.trade.recipientId !== state.id) return undefined;
  if (args.trade.status !== 'countered') return undefined;
  if (!counterCardId || !offeredCardId) return undefined;
  if (!cardsIsCardValid(counterCardId)) return undefined;
  if (!cardsIsCardValid(offeredCardId)) return undefined;
  if (cardsGetTradeExpiresAt(args.trade) <= args.now) return undefined;
  if (!cardsCanOpenTrade(state, args.now)) return undefined;
  if (!cardsCanUseCardInTrade(state, counterCardId, args.now)) {
    return undefined;
  }

  const pendingTrades = cardsEnsureState(state).cardTrades.pendingTrades!;
  if (pendingTrades[args.trade.tradeId]) return undefined;

  const otherUserProfile = getPlayerProfile(
    state,
    args.otherUserName,
    args.otherUserAvatar,
  );
  const trade: CardPendingTrade = {
    ...args.trade,
    otherUserName: otherUserProfile.name,
    otherUserAvatar: otherUserProfile.photo,
  };

  cardsRemoveCard(state, { cardId: counterCardId, now: args.now });
  pendingTrades[trade.tradeId] = trade;
  return trade;
}

/**
 * Add or update an incoming pending trade from another user.
 * Expired trades are rejected, completed recipient markers are preserved, and
 * new trades require available open-trade capacity.
 *
 * @param state Mutable recipient or proposer state receiving the incoming trade.
 * @param trade Pending trade to store.
 * @param now Current timestamp used for cleanup and expiration checks.
 * @returns True when the trade was accepted into pending state.
 */
export function cardsAddIncomingTrade(
  state: MutableState,
  trade: CardPendingTrade,
  now: number,
): boolean {
  cardsCleanupExpiredTrades(state, now);
  if (cardsGetTradeExpiresAt(trade) <= now) return false;

  const pendingTrades = cardsEnsureState(state).cardTrades.pendingTrades!;
  const existingTrade = pendingTrades[trade.tradeId] as
    | CardPendingTrade
    | undefined;
  if (existingTrade?.proposerConfirmedAt) return false;

  if (!existingTrade && !cardsCanOpenTrade(state, now)) {
    return false;
  }

  const otherUserProfile = getPlayerProfile(
    state,
    trade.otherUserName,
    trade.otherUserAvatar,
  );

  pendingTrades[trade.tradeId] = {
    ...trade,
    otherUserName: otherUserProfile.name,
    otherUserAvatar: otherUserProfile.photo,
  };
  return true;
}

/**
 * Counter a proposed trade as the recipient.
 * The counter card is escrowed by removing it from inventory, and the pending
 * trade is marked as countered. Answering a trade does not count against card
 * trade limits.
 *
 * @param state Mutable recipient state.
 * @param args Counter details.
 * @param args.tradeId Trade id to counter.
 * @param args.cardId Card id offered back to the proposer.
 * @param args.now Timestamp used for validation and trade updates.
 * @returns The updated countered trade.
 * @throws When the trade is missing, belongs to another recipient, is not
 * proposed, has invalid offered cards, or fails inventory checks.
 */
export function cardsCounterTrade(
  state: MutableState,
  args: {
    tradeId: string;
    cardId: CardId;
    now: number;
  },
): CardPendingTrade {
  const trade = cardsGetPendingTrade(state, args.tradeId, args.now);
  if (!trade) throw new Error('Card trade not found.');

  if (trade.recipientId !== state.id) {
    throw new Error('Only the trade recipient can counter this trade.');
  }

  if (trade.status !== 'proposed') {
    throw new Error('Card trade cannot be countered.');
  }

  const offeredCardId = trade.offeredCardIds[0];
  if (
    trade.offeredCardIds.length !== 1 ||
    !offeredCardId ||
    !cardsIsCardValid(offeredCardId)
  ) {
    throw new Error('Card trade has an invalid offered card.');
  }

  if (!cardsCanUseCardInTrade(state, args.cardId, args.now)) {
    throw new Error(`Not enough copies of ${args.cardId} to trade.`);
  }

  const updatedTrade: CardPendingTrade = {
    ...trade,
    counterCardIds: [args.cardId],
    status: 'countered',
    recipientConfirmedAt: args.now,
    updatedAt: args.now,
  };

  cardsRemoveCard(state, { cardId: args.cardId, now: args.now });
  cardsEnsureState(state).cardTrades.pendingTrades![args.tradeId] =
    updatedTrade;
  return updatedTrade;
}

/**
 * Apply a recipient's counter update to the proposer-side pending trade.
 * This records the recipient's selected counter cards without moving inventory
 * on the proposer.
 *
 * @param state Mutable proposer state.
 * @param args Counter data received from the recipient.
 * @param args.tradeId Trade id to update.
 * @param args.counterCardIds Counter card ids selected by the recipient.
 * @param args.recipientConfirmedAt Timestamp when the recipient countered.
 * @param args.updatedAt Timestamp to store on the pending trade.
 * @param args.now Current timestamp used to find an actionable pending trade.
 * @returns The updated trade, or undefined when it cannot be applied.
 */
export function cardsApplyTradeCounter(
  state: MutableState,
  args: {
    tradeId: string;
    counterCardIds: CardId[];
    recipientConfirmedAt: number;
    updatedAt: number;
    now: number;
  },
): CardPendingTrade | undefined {
  const trade = cardsGetPendingTrade(state, args.tradeId, args.now);
  if (!trade) return undefined;
  if (trade.proposerId !== state.id) return undefined;

  const updatedTrade: CardPendingTrade = {
    ...trade,
    counterCardIds: args.counterCardIds,
    status: 'countered',
    recipientConfirmedAt: args.recipientConfirmedAt,
    updatedAt: args.updatedAt,
  };

  cardsEnsureState(state).cardTrades.pendingTrades![args.tradeId] =
    updatedTrade;
  return updatedTrade;
}

/**
 * Cancel an unfinished pending trade and release this user's escrowed cards.
 * Completed recipient markers cannot be cancelled through this path.
 *
 * @param state Mutable user state containing the pending trade.
 * @param tradeId Trade id to cancel.
 * @returns The cancelled trade, or undefined when no cancellable trade exists.
 */
export function cardsCancelTrade(
  state: MutableState,
  tradeId: string,
): CardPendingTrade | undefined {
  const pendingTrades = state.cards?.cardTrades?.pendingTrades;
  const trade = pendingTrades?.[tradeId];
  if (!pendingTrades || !trade) return undefined;
  if (trade.proposerConfirmedAt) return undefined;

  cardsReturnTradeCards(state, tradeId);
  delete pendingTrades[tradeId];
  return trade;
}

/**
 * Complete a countered trade as the original proposer.
 * The recipient's escrowed counter cards are queued in the proposer's received
 * ledger, the returned completed trade is marked with `proposerConfirmedAt`,
 * and the pending trade is removed.
 *
 * @param state Mutable proposer state.
 * @param tradeId Countered trade id to complete.
 * @param now Timestamp used for validation and completion.
 * @returns Completed trade details, or undefined when completion is not valid.
 */
export function cardsCompleteTradeAsProposer(
  state: MutableState,
  tradeId: string,
  now: number,
): CardPendingTrade | undefined {
  const trade = cardsGetPendingTrade(state, tradeId, now);
  if (!trade || trade.proposerId !== state.id || trade.status !== 'countered') {
    return undefined;
  }

  const counterCardIds = (trade.counterCardIds ?? []) as CardId[];
  if (counterCardIds.length === 0) return undefined;

  for (const cardId of counterCardIds) {
    // Trade transfers do not consume the card receive limit.
    cardsQueueCardReceived(state, {
      cardId,
      userId: trade.recipientId,
    });
  }

  const completedTrade: CardPendingTrade = {
    ...trade,
    proposerConfirmedAt: now,
    updatedAt: now,
  };
  const pendingTrades = state.cards?.cardTrades?.pendingTrades;
  if (pendingTrades) delete pendingTrades[tradeId];
  return completedTrade;
}

/**
 * Complete a countered trade as the recipient.
 * The proposer's escrowed offered cards are queued in the recipient's received
 * ledger, and the completed trade remains in pending trades as a marker for
 * proposer verification.
 *
 * @param state Mutable recipient state.
 * @param tradeId Countered trade id to complete.
 * @param now Timestamp used for validation and completion.
 * @returns Completed trade marker, or undefined when completion is not valid.
 */
export function cardsCompleteTradeAsRecipient(
  state: MutableState,
  tradeId: string,
  now: number,
): CardPendingTrade | undefined {
  const trade = cardsGetPendingTrade(state, tradeId, now);
  if (
    !trade ||
    trade.recipientId !== state.id ||
    trade.status !== 'countered'
  ) {
    return undefined;
  }

  const counterCardIds = (trade.counterCardIds ?? []) as CardId[];
  if (counterCardIds.length === 0) return undefined;

  for (const cardId of trade.offeredCardIds as CardId[]) {
    // Trade transfers do not consume the card receive limit.
    cardsQueueCardReceived(state, {
      cardId,
      userId: trade.proposerId,
    });
  }

  const completedTrade: CardPendingTrade = {
    ...trade,
    proposerConfirmedAt: now,
    updatedAt: now,
  };
  const pendingTrades = state.cards?.cardTrades?.pendingTrades;
  if (pendingTrades) pendingTrades[tradeId] = completedTrade;
  return completedTrade;
}

/**
 * Remove one card from the user's owned inventory.
 * Empty owned-card and newly-added entries are cleaned up after decrementing.
 *
 * @param state Mutable user state containing cards state.
 * @param args Card removal details.
 * @param args.cardId Card id to remove.
 * @param args.now Timestamp associated with the removal.
 */
export function cardsRemoveCard(
  state: MutableState,
  args: {
    cardId: CardId;
    now: number;
  },
): void {
  const cards = cardsEnsureState(state);

  if (cards.cardsOwned[args.cardId] === undefined) return;

  cards.cardsOwned[args.cardId] -= 1;

  if (cards.cardsOwned[args.cardId] <= 0) {
    delete cards.cardsOwned[args.cardId];
    delete cards.cardsNewlyAdded[args.cardId];
  }

  if (cards.cardsNewlyAdded[args.cardId] !== undefined) {
    cards.cardsNewlyAdded[args.cardId] -= 1;
    if (cards.cardsNewlyAdded[args.cardId] <= 0) {
      delete cards.cardsNewlyAdded[args.cardId];
    }
  }
}

/**
 * Queue a traded card for the collection popup without touching receive-limit
 * accounting.
 *
 * `cards.cardsReceived` is the pending inbox that later moves into owned
 * inventory through `cardsCollectAllCardsReceived`. Trade transfers bypass the
 * rolling receive-limit history used by `cardsCanReceiveCard`.
 */
function cardsQueueCardReceived(
  state: MutableState,
  args: {
    cardId: CardId;
    userId: string;
  },
): void {
  const cards = cardsEnsureState(state);

  if (!cards.cardsReceived[args.userId]) {
    cards.cardsReceived[args.userId] = {};
  }

  if (!cards.cardsReceived[args.userId][args.cardId]) {
    cards.cardsReceived[args.userId][args.cardId] = 0;
  }

  cards.cardsReceived[args.userId][args.cardId] += 1;
}

/**
 * Add a card to the received-card ledger.
 * This does not affect owned inventory until the user collects received cards.
 * If the receive limit is full, the card is discarded and counted.
 *
 * @param state Mutable recipient state.
 * @param args Received-card details.
 * @param args.cardId Card id received.
 * @param args.userId Sender player id.
 * @param args.now Timestamp used for receive-limit history.
 */
export function cardsAddCardReceived(
  state: MutableState,
  args: {
    cardId: CardId;
    userId: string;
    now: number;
  },
): void {
  const cards = cardsEnsureState(state);

  if (!cardsCanReceiveCard(state, args.now)) {
    cards.cardTrades.cardsDiscarded++;
    return;
  }

  cardsQueueCardReceived(state, args);

  cards.cardTrades.cardsReceived = [
    ...cardsGetTradeReceivedForLimit(state, args.now),
    {
      cardId: args.cardId,
      timestamp: args.now,
    },
  ];
}

/**
 * Move every received-card ledger entry into owned inventory.
 * The received-card ledger and discarded-card counter are cleared afterward.
 *
 * @param state Mutable user state containing received cards.
 * @param now Timestamp associated with the inventory grants.
 */
export function cardsCollectAllCardsReceived(
  state: MutableState,
  now: number,
): void {
  const cards = cardsEnsureState(state);

  for (const userId in cards.cardsReceived) {
    for (const cardId in cards.cardsReceived[userId]) {
      const count = cards.cardsReceived[userId][cardId];
      for (let i = 0; i < count; i++) {
        cardsAddCard(state, { cardId: cardId as CardId, now });
      }
    }
  }

  cards.cardsReceived = {};
  cards.cardTrades.cardsDiscarded = 0;
}

/**
 * Clear newly-added card markers after the user has seen them.
 * When ids are provided, only those markers are cleared; otherwise all markers
 * are cleared.
 *
 * @param state Mutable user state containing newly-added card markers.
 * @param ids Optional card ids to clear individually.
 */
export function cardsClearNewlyAdded(
  state: MutableState,
  ids?: CardId[],
): void {
  const cards = cardsEnsureState(state);

  if (!ids) {
    cards.cardsNewlyAdded = {};
    return;
  }

  for (const id of ids) {
    if (cards.cardsNewlyAdded[id]) {
      delete cards.cardsNewlyAdded[id];
    }
  }
}

/**
 * Mark a collection or album reward as claimed when it is available.
 *
 * @param state Mutable user state containing owned cards and claimed rewards.
 * @param rewardId Collection id or `album` reward id to claim.
 * @returns Claimed rewards, or undefined when the reward is unavailable.
 */
export function cardsClaimRewards(
  state: MutableState,
  rewardId: CardsRewardId,
): Match2Reward[] | undefined {
  const cards = state.cards;
  if (!cards) return undefined;

  const status = cardsGetRewardsStatus(state, rewardId);
  if (status !== 'available') return undefined;

  const rewards =
    rewardId === 'album'
      ? cardsAlbumCompleteRewards
      : cardCollections[rewardId].rewards;

  cards.rewardsClaimed = cards.rewardsClaimed || {};
  cards.rewardsClaimed[rewardId] = true;

  return rewards as Match2Reward[];
}
